import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';

import SocialMediaShareButtons from '../utilities/SocialMediaShareButtons';
import { useAppDispatch } from '../../types/hooks';
import { Post } from '../../types/types';
import { changePlayerInfo, changePodcastInfo } from '../../slices/AppSlice';
import { getUyFormattedDate, downloadAudio } from '../../utils/utilities';
import CircularSpinner from '../../components/utilities/CircularSpinner';
import { NOTES_IDS_KEY } from '../../utils/localStorage';
import ShareIcon from '../../images/compartir.svg';
import PlayIcon from '../../images/play-blanco.svg';
import DescargarIcon from '../../images/descargar.svg';

const StyledPostContentContainer = styled.div`
  .photographer {
    margin: 0.5rem 1rem 0;
    font-family: 'Roboto Light';
    font-size: 0.75rem;
    color: var(--text-gray);
    margin-bottom: 0.25rem;
    padding-bottom: 0.25rem;
  }
  .listen-and-share-container {
    display: flex;
    align-items: center;
    font-family: 'Roboto Light';
    border-bottom: 1px solid var(--border-gray);
    margin: 0.5rem 0;
    border-top: 1px solid var(--border-gray);
    .listen-button {
      min-height: 2.4rem;
      background-color: black;
      padding: 0.5rem 1rem;
      display: flex;
      align-items: center;
      font-family: 'TradeGothicN2';
      color: white;
      font-size: 1.1rem;
      img {
        width: 1.2rem;
        height: 1.2rem;
        margin-right: 0.5rem;
      }
    }
    .download-container {
      background-color: #8c8b8b;
      min-height: 2.4rem;
      min-width: 2.2rem;
      button {
        padding: 0.5rem;
        img {
          width: 1.22rem;
          height: 1.22rem;
        }
      }
    }
    .compartir-btn-container {
      flex-grow: 1;
      display: flex;
      justify-content: flex-end;
      margin-right: 1rem;
      button {
        display: flex;
        align-items: center;
        font-weight: bold;
        font-size: 0.8rem;
        img {
          margin-left: 0.25rem;
        }
      }
    }
  }

  .post-content-container {
    margin: 0rem 1rem;

    @media (min-width: 576px) {
      margin: 0;
    }
    strong {
      font-family: 'Roboto Bold';
      font-weight: normal;
    }
    .date-container {
      font-family: 'Roboto Light';
      font-size: 0.9rem;
      margin-top: 1.5rem;
    }
    .post-content {
      font-family: 'Roboto Light';
      padding-top: 1.5rem;
      margin: 0 0.2rem;
      p {
        margin-bottom: 1rem;
        line-height: 1.5;
      }
      div [class*='heateor'],
      div:empty {
        display: none;
      }
      img {
        width: 100%;
        height: 100%;
      }
      iframe {
        max-width: 90vw;
        width: 100%;
      }
      .wp-video,
      > video {
        width: 100% !important;
      }
      a {
        text-decoration: underline;
      }
    }
  }
`;

type PostContentProps = {
  post: Post;
  mainCategorySlug: string;
};

const PostContent = ({ post, mainCategorySlug }: PostContentProps) => {
  const dispatch = useAppDispatch();
  const [areSocialIconsDisplayed, setAreSocialIconsDisplayed] = useState<boolean>(false);
  const [postDate, setPostDate] = useState<string>();
  const [fetching, setFetching] = useState<boolean>(false);
  const [isNoteSaved, setIsNoteSaved] = useState<boolean>(false);

  useEffect(() => {
    setPostDate(getUyFormattedDate(new Date(post.date)));
    const notesJsonIds = localStorage.getItem(NOTES_IDS_KEY);

    if (notesJsonIds && post.databaseId) {
      const notesIds: number[] = JSON.parse(notesJsonIds);

      setIsNoteSaved(notesIds.includes(post.databaseId));
    }
  }, []);

  const downloadPostAudio = (url: string, name: string) => {
    downloadAudio({ fetching, setFetching, url, name });
  };

  const saveNote = useCallback(() => {
    const notesJsonIds = localStorage.getItem(NOTES_IDS_KEY);
    const noteId: number | undefined = post.databaseId;

    if (!notesJsonIds) {
      localStorage.setItem(NOTES_IDS_KEY, JSON.stringify([noteId]));
      setIsNoteSaved(true);
    } else if (noteId) {
      const notesIds: number[] = JSON.parse(notesJsonIds);

      if (!notesIds.includes(noteId)) {
        notesIds.push(noteId);
        localStorage.setItem(NOTES_IDS_KEY, JSON.stringify(notesIds));
        setIsNoteSaved(true);
      }
    }
  }, [post]);

  return (
    <StyledPostContentContainer>
      {post.afc_post && post.afc_post.nombreFotografo && (
        <p className="photographer">{post.afc_post.nombreFotografo}</p>
      )}
      <div className="listen-and-share-container">
        {post.afc_post.audioDeLaNota && (
          <>
            <button
              className="listen-button"
              onClick={() => {
                dispatch(
                  changePlayerInfo({
                    playerUrl: post.afc_post.audioDeLaNota.mediaItemUrl,
                    playerSlug: mainCategorySlug,
                    timeStamp: new Date().getUTCMilliseconds(),
                  }),
                );
                dispatch(
                  changePodcastInfo({
                    podcastTitle: post.title,
                    podcastSlug: post.slug,
                  }),
                );
              }}
            >
              <img alt="Icono de esuchar la nota" src={PlayIcon} />
              Escuchar
            </button>
            <div className="download-container">
              {fetching ? (
                <CircularSpinner height={1.6} margin="0.5rem 0.4rem" width={1.6} />
              ) : (
                <button
                  aria-label="Botón de descagar"
                  className={`${fetching ? 'disabled' : ''}`}
                  onClick={() =>
                    downloadPostAudio(post.afc_post.audioDeLaNota.mediaItemUrl, post.title)
                  }
                >
                  <img alt="Descargar icono" src={DescargarIcon} />
                </button>
              )}
            </div>
          </>
        )}
        <div className="compartir-btn-container">
          <button onClick={() => setAreSocialIconsDisplayed(!areSocialIconsDisplayed)}>
            Compartir <img alt="Icono de compartir noticia" src={ShareIcon} />
          </button>
        </div>
        <SocialMediaShareButtons
          displayed={areSocialIconsDisplayed}
          post={post}
          toggleDisplayed={setAreSocialIconsDisplayed}
        />
      </div>
      <div className="post-content-container">
        <p className="date-container">{postDate}</p>
        {/* Publicidad principal NOTICIAS Mobile */}
        {post.content && (
          <div className="post-content" dangerouslySetInnerHTML={{ __html: post.content }} />
        )}
      </div>
    </StyledPostContentContainer>
  );
};

export default PostContent;
